import React, {
  BaseSyntheticEvent,
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

import PageContent from 'components/library/PageContent';
import Pagination from 'components/library/Pagination';
import Loader from 'components/library/Loader';
import Table from 'components/library/Table';
import Input from 'components/library/Input';
import Button from 'components/library/Button';
import AddCustomerModal from 'components/PageCustomers/components/AddCustomerModal';
import {
  BLOCK_FILTER_OPTIONS,
  MIN_PAGE,
  NAME_ORDER_OPTIONS,
  PAGE_SIZE_OPTIONS,
  TITLE_ORDER_OPTIONS,
} from 'constants/constants';
import strings from 'constants/localization';
import {
  ERoles, TFilterBlocked, TPageSizeOptionsItem, TUser
} from 'types';
import { useUsersFind } from 'hooks/useUser';
import UserContext from 'contexts/ContextUser';
import Select from 'components/library/Select';
import { ICallback } from 'components/library/Table/components/ActionHeader';
import EmptyState, { EmptyStates } from 'components/library/EmptyState';
import { TSort } from 'components/library/Table/types';
import get from 'lodash/get';
import { Location } from 'history';
import { NavigateFunction } from 'react-router/lib/hooks';
import useColumns from './useColumns';
import css from './PageCustomers.module.css';
import BlockUserModal from '../Modals/BlockUserModal';
import useQueryParams from '../../hooks/useQueryParams';
import {
  isManager, showEmptyState, navigateWithState, isAppEventbriteManager
} from '../../helpers';
import AddEventModal from './components/AddEventModal';

export interface IStateParams {
  managerId?: string
  page?: number
  search: string
  order?: TSort
  limit?: TPageSizeOptionsItem
}

export enum EEntityType {
  CUSTOMER = 'customer',
  EVENT = 'event',
}

interface ICustomers {
  type: EEntityType
  state: IStateParams
  role: ERoles
  pspId: string | undefined
  location: Location
  navigate: NavigateFunction
}

const Customers: React.FC<ICustomers> = props => {
  const {
    state,
    role,
    location,
    navigate,
    pspId,
    type,
  } = props;

  const [filterBlocked, setFilterBlocked] = useState<TFilterBlocked | undefined>(BLOCK_FILTER_OPTIONS[1]);

  const [limit, setLimit] = useState<{ label: number, value: number }>(state.limit || PAGE_SIZE_OPTIONS[0]);
  useEffect(() => setLimit(state.limit || PAGE_SIZE_OPTIONS[0]), [state.limit]);

  const [searchActive, setSearchActive] = useState<boolean>(!!state.search);
  const [search, setSearchText] = useState<string>(state.search);
  useEffect(() => {
    setSearchText(state.search);
    if (state.search) {
      setSearchActive(true);
    }
  }, [state.search]);

  const [page, setPage] = useState<number>(state.page || MIN_PAGE);
  useEffect(() => setPage(state.page || MIN_PAGE), [state.page]);

  const [userToBlock, setUserToBlock] = useState<TUser | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onChangeLimit = useCallback((value:SingleValue<{ label: number, value: number}>) => {
    if (limit !== value) {
      setLimit(value || PAGE_SIZE_OPTIONS[0]);
      setPage(MIN_PAGE);
      navigateWithState(navigate, location, { page: MIN_PAGE, limit: value });
    }
  }, [limit, setLimit, setPage, location]);

  const onChangePage = useCallback((event: any, value:number) => {
    if (page !== value) {
      setPage(value);
      navigateWithState(navigate, location, { page: value });
    }
  }, [page, setPage, location]);

  const navigateToCustomerProfile = useCallback<ICallback>(
    id => navigate(id + location.search),
    [navigate, location.search]
  );
  const onBlock = useCallback<ICallback>((id, user) => setUserToBlock(user), []);
  const closeBlockModal = useCallback(() => setUserToBlock(undefined), [setUserToBlock]);

  // TODO: set filters blocked
  const [order, setOrder] = useState<TSort>(state.order || NAME_ORDER_OPTIONS[2].value);
  useEffect(() => setOrder(state.order || TITLE_ORDER_OPTIONS[2].value), [state.order]);

  const limitValue = limit?.value || PAGE_SIZE_OPTIONS[0].value;

  const {
    data = { data: [], total: 0 },
    isLoading,
    error
  } = useUsersFind({
    managerId: state.managerId,
    includeArticleStats: true,
    includeManager: true,
    includeOrdersStats: true,
    limit: limitValue,
    blocked: filterBlocked?.value,
    skip: (page - 1) * limitValue,
    search,
    role: 'customer',
    sort: order,
    pspId,
  });

  const handleSetOrder = useCallback((value: TSort): void => {
    setOrder(value);
    navigateWithState(navigate, location, { order: value });
  }, [location, setOrder]);

  const columns = useColumns({
    order, setOrder: handleSetOrder, filterBlocked, setFilterBlocked, onBlock, navigateToCustomerProfile, role, type
  });

  const handleSearchTextChange = useCallback((e: BaseSyntheticEvent): void => {
    const { value } = e.target;
    setPage(MIN_PAGE);
    setSearchText(value);
    navigateWithState(navigate, location, { page: MIN_PAGE, search: value }, { replace: true });
  }, [setPage, setSearchText, location]);

  const toggleSearchActive = () => {
    setSearchActive(prev => !prev);
  };

  const toggleModalOpen = (): void => {
    setModalOpen(prev => !prev);
  };

  const disabled:boolean = isLoading || !!error;

  const pageTitle = useMemo<string>(
    () => type === EEntityType.CUSTOMER ? strings.customersPageTitle : strings.eventsPageTitle,
    [type]
  );

  const buttonAddLabel = useMemo<string>(
    () => type === EEntityType.CUSTOMER ? strings.customersPageButtonAddCustomer : strings.eventsPageButtonAddEvent,
    [type]
  );

  const modal = useMemo(() => {
    switch (type) {
      case EEntityType.CUSTOMER:
        return (<AddCustomerModal isOpen={modalOpen} toggleOpen={toggleModalOpen} />);
      case EEntityType.EVENT:
        return (<AddEventModal isOpen={modalOpen} toggleOpen={toggleModalOpen} />);
      default:
        return (<Loader />);
    }
  }, [type, modalOpen, toggleModalOpen]);

  return (
    <PageContent pageTitle={pageTitle} withBreadcrumbs>
      <div className={css.container}>
        <div className={css.header}>
          <Select isDisabled={disabled} className={css.select} value={limit} onChange={onChangeLimit} options={PAGE_SIZE_OPTIONS} isSearchable={false} />
          <div className={css.right}>
            <Input
              className={css.search}
              searchActive={searchActive}
              toggleSearchActive={toggleSearchActive}
              onChange={handleSearchTextChange}
              value={search}
            />
            <Button buttonType="primary" onClick={toggleModalOpen}>{buttonAddLabel}</Button>
          </div>
        </div>
        <div className={css.table}>
          <Table<TUser>
            data={data.data}
            columns={columns}
          />
        </div>
        {showEmptyState(!isLoading, data.data.length, !search) && (
          <div className={css.noContent}>
            <EmptyState
              label={strings.formatString(strings.emptyStateOnPageTitle, pageTitle) as string}
              description={strings.formatString(strings.emptyStateOnPageDescription, <b>{buttonAddLabel}</b>) as string}
              variant={EmptyStates.NO_CUSTOMERS}
            >
              <Button buttonType="primary" onClick={toggleModalOpen}>{buttonAddLabel}</Button>
            </EmptyState>
          </div>
        )}
        {showEmptyState(!isLoading, data.data.length, !!search) && (
          <div className={css.noContent}>
            <EmptyState label={strings.emptyStateTitleNoResults} description={strings.emptyStateTitleNoResultsDescription} variant={EmptyStates.NO_RESULTS} />
          </div>
        )}
        {isLoading && <div className={css.loader}><Loader /></div> }
        <Pagination
          className={css.pagination}
          page={page}
          count={Math.ceil(data.total / limitValue) || undefined}
          onChange={onChangePage}
        />
        { modal }
        <BlockUserModal
          isOpen={!!userToBlock}
          closeModal={closeBlockModal}
          userToBlock={userToBlock}
        />
      </div>
    </PageContent>
  );
};

const Container = () => {
  const query = useQueryParams();
  const location = useLocation();
  const navigate = useNavigate();
  const contextUser = useContext(UserContext);
  const pspId = get(contextUser, 'user.pspId');
  const permissions = get(contextUser, 'user.userPermissions', []);

  const role = contextUser.user?.role as ERoles;

  const params = useMemo<IStateParams>(() => ({
    managerId: query.get('managerId') || undefined,
    page: parseInt(get(location.state, 'page', query.get('page')) || '', 10) || undefined,
    limit: get(location.state, 'limit'),
    search: get(location.state, 'search', ''),
    order: get(location.state, 'order'),
  }), [location, query]);

  if (isManager(permissions)) {
    params.managerId = get(contextUser, 'user.id');
  }

  const entityType = useMemo<EEntityType>(
    () => isAppEventbriteManager(permissions) ? EEntityType.EVENT : EEntityType.CUSTOMER,
    [permissions]
  );

  return useMemo(() => (
    <Customers
      type={entityType}
      state={params}
      role={role}
      location={location}
      navigate={navigate}
      pspId={pspId}
    />
  ), [entityType, params, role, location, navigate, pspId]);
};

export default Container;
